import React from 'react';
import {useIntl} from 'react-intl';
import {IconText, Table} from '@/web-components';
import MonthByMonthProjectLoader, {monthByMonthProjectLoaderQuery} from './MonthByMonthProjectLoader';
import {
	canExpandDeliverableRow,
	filtersToSearchQuery,
	getBreakdownDeliverableId,
	getDeliverableName,
} from '../ProjectPortfolioReportUtils';
import ProjectPortfolioMonthByMonthDownload, {
	projectPortfolioMonthByMonthDownloadQuery,
} from './ProjectPortfolioMonthByMonthDownload';
import {NO_ID} from '../../../../constants';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {LoadMore} from '../../../loaders/LoadMore';
import ProjectPortfolioMonthByMonthRow from './ProjectPortfolioMonthByMonthRow';
import Util from '../../../../forecast-app/shared/util/util';
import DeprecatedProjectIndicatorJS from '../../../../forecast-app/shared/components/project-indicator/js/DeprecatedProjectIndicatorJS';
import MonthByMonthDeliverableLoader, {monthByMonthDeliverableLoaderQuery} from './MonthByMonthDeliverableLoader';
import MonthByMonthPhaseRoleCascade from './MonthByMonthPhaseRoleCascade';

const ProjectPortfolioMonthByMonthProjectRows = ({
	setCsvDownloadFunction,
	hasBaseline,
	pageSize,
	startDate,
	endDate,
	filters,
	boolEyeOptions,
	levelProps,
	timeSearchQuery,
	expenseSearchQuery,
	selectedSingleValue,
	projectGroupId,
	programId,
	clientId,
	useWinProbability,
}) => {
	const intl = useIntl();

	const addedFilters = [];
	if (projectGroupId) {
		const isNoConnectedProject = Util.getUUIdFromBase64String(projectGroupId) === NO_ID;
		addedFilters.push({key: 'projectGroup', operator: 'IS', value: isNoConnectedProject ? [null] : projectGroupId});
	}
	if (programId) {
		const isNoProgram = Util.getUUIdFromBase64String(programId) === NO_ID;
		addedFilters.push({key: 'program', operator: 'IS', value: isNoProgram ? [null] : programId});
	}
	if (clientId) {
		const isNoClient = Util.getUUIdFromBase64String(clientId) === NO_ID;
		addedFilters.push({key: 'client', operator: 'IS', value: isNoClient ? [null] : clientId});
	}

	return (
		<LoadMore
			key="query-render-month-by-month-project"
			query={monthByMonthProjectLoaderQuery}
			variables={{
				searchQuery: filtersToSearchQuery(filters, addedFilters),
				timeSearchQuery: timeSearchQuery,
				expenseSearchQuery: expenseSearchQuery,
				startYear: startDate.year(),
				startMonth: startDate.month() + 1,
				startDay: startDate.date(),
				endYear: endDate.year(),
				endMonth: endDate.month() + 1,
				endDay: endDate.date(),
				pageSize: pageSize,
				selectedSingleValue: selectedSingleValue,
				ignoreBaselineWinChance: !useWinProbability,
				excludeOpportunityActuals: true,
			}}
			loader={<MonthByMonthProjectLoader />}
		>
			{({data, loadMore}) => {
				if (!projectGroupId && !programId && !clientId) {
					setCsvDownloadFunction(() => {
						showModal({
							type: MODAL_TYPE.DOWNLOADING_CSV_DATA,
							componentName: 'project_portfolio_month_by_month_project_csv_loader',
							query: projectPortfolioMonthByMonthDownloadQuery,
							variables: {
								searchQuery: filtersToSearchQuery(filters),
								timeSearchQuery: timeSearchQuery,
								expenseSearchQuery: expenseSearchQuery,
								startYear: startDate.year(),
								startMonth: startDate.month() + 1,
								startDay: startDate.date(),
								endYear: endDate.year(),
								endMonth: endDate.month() + 1,
								endDay: endDate.date(),
								pageSize: 1000000,
								selectedSingleValue: selectedSingleValue,
								ignoreBaselineWinChance: !useWinProbability,
								excludeOpportunityActuals: true,
							},
							createCsvDownload: <ProjectPortfolioMonthByMonthDownload />,
							startDate,
							endDate,
							boolEyeOptions,
							selectedSingleValue,
						});
					});
				}
				return (
					<Table.Rows data={data} canExpand={true} loadMore={loadMore} autoload={true} {...levelProps}>
						{({rowData, tableColumnsProps, expanded, nextLevelProps: phaseLevelProps}) => {
							const fullAccessToProject = rowData?.project?.fullAccessToProject;
							const fixedPriceLocks = rowData?.project?.fixedPriceLocks;
							const withProject = data => ({...data, project: rowData.project});

							return (
								<>
									<ProjectPortfolioMonthByMonthRow
										rowData={rowData}
										tableColumnProps={tableColumnsProps}
										headerColumn={
											<IconText
												key={rowData.project.id}
												text={
													fullAccessToProject
														? rowData.project.name
														: intl.formatMessage({id: 'common.no_project_access'})
												}
											>
												<DeprecatedProjectIndicatorJS
													project={rowData.project}
													disableLink={rowData.project.readOnlyAccess}
												/>
											</IconText>
										}
										selectedSingleValue={selectedSingleValue}
										hasBaseline={hasBaseline}
										fixedPriceLocks={fixedPriceLocks}
										useWinProbability={useWinProbability}
									/>
									{expanded &&
										(rowData.project.useDeliverables ? (
											<LoadMore
												key="query-render-month-by-month-deliverable"
												query={monthByMonthDeliverableLoaderQuery}
												loader={<MonthByMonthDeliverableLoader />}
												variables={{
													projectId: rowData.project.id,
													timeSearchQuery: timeSearchQuery,
													expenseSearchQuery: expenseSearchQuery,
													startYear: startDate.year(),
													startMonth: startDate.month() + 1,
													startDay: startDate.date(),
													endYear: endDate.year(),
													endMonth: endDate.month() + 1,
													endDay: endDate.date(),
													selectedSingleValue: selectedSingleValue,
													ignoreBaselineWinChance: !useWinProbability,
													excludeOpportunityActuals: true,
												}}
											>
												{({data: deliverableBreakdownData}) => {
													return (
														<Table.Rows
															data={deliverableBreakdownData}
															{...phaseLevelProps}
															canExpand={true}
														>
															{({
																rowData: deliverableRowData,
																tableColumnsProps: deliverableTableColumnProps,
																expanded: deliverableExpanded,
																nextLevelProps: nestedPhaseLevelProps,
															}) => {
																const deliverableId =
																	getBreakdownDeliverableId(deliverableRowData);
																return (
																	<>
																		<ProjectPortfolioMonthByMonthRow
																			rowData={withProject(deliverableRowData)}
																			tableColumnProps={deliverableTableColumnProps}
																			headerColumn={getDeliverableName(
																				intl,
																				deliverableRowData
																			)}
																			canExpand={canExpandDeliverableRow(
																				deliverableRowData
																			)}
																			selectedSingleValue={selectedSingleValue}
																			fixedPriceLocks={fixedPriceLocks}
																			useWinProbability={useWinProbability}
																		/>
																		{deliverableExpanded && (
																			<MonthByMonthPhaseRoleCascade
																				intl={intl}
																				projectId={rowData.project.id}
																				deliverableId={deliverableId}
																				startDate={startDate}
																				endDate={endDate}
																				timeSearchQuery={timeSearchQuery}
																				expenseSearchQuery={expenseSearchQuery}
																				selectedSingleValue={selectedSingleValue}
																				useWinProbability={useWinProbability}
																				fixedPriceLocks={fixedPriceLocks}
																				levelProps={nestedPhaseLevelProps}
																				withProject={withProject}
																			/>
																		)}
																	</>
																);
															}}
														</Table.Rows>
													);
												}}
											</LoadMore>
										) : (
											<MonthByMonthPhaseRoleCascade
												intl={intl}
												projectId={rowData.project.id}
												deliverableId={undefined}
												startDate={startDate}
												endDate={endDate}
												timeSearchQuery={timeSearchQuery}
												expenseSearchQuery={expenseSearchQuery}
												selectedSingleValue={selectedSingleValue}
												useWinProbability={useWinProbability}
												fixedPriceLocks={fixedPriceLocks}
												levelProps={phaseLevelProps}
												withProject={withProject}
											/>
										))}
								</>
							);
						}}
					</Table.Rows>
				);
			}}
		</LoadMore>
	);
};

export default ProjectPortfolioMonthByMonthProjectRows;
